export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private videoChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  async start(): Promise<MediaStream> {
    try {
      // Request camera with mobile-optimized constraints
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          facingMode: 'user',
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      // Find best supported MIME type - prioritize webm for mobile compatibility
      const mimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
      ];
      
      let selectedMimeType = 'video/webm';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      console.log('Selected MIME type:', selectedMimeType);
      
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: selectedMimeType,
      });
      
      this.videoChunks = [];
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.videoChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };
      
      this.mediaRecorder.start(1000); // Collect data every second
      console.log('Video recording started with mimeType:', selectedMimeType);
      
      return this.stream;
    } catch (error: any) {
      console.error('Error starting video recording:', error);
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError') {
        throw new Error('Izin kamera ditolak. Silakan aktifkan izin kamera di pengaturan browser.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('Kamera tidak ditemukan. Pastikan perangkat memiliki kamera.');
      } else if (error.name === 'OverconstrainedError') {
        throw new Error('Kamera tidak mendukung resolusi yang diminta.');
      } else if (error.name === 'NotReadableError') {
        throw new Error('Kamera sedang digunakan aplikasi lain.');
      }
      
      throw error;
    }
  }

  async stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('MediaRecorder not initialized'));
        return;
      }

      if (this.mediaRecorder.state === 'inactive') {
        reject(new Error('MediaRecorder is not recording'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const mimeType = this.mediaRecorder?.mimeType || 'video/webm';
        const videoBlob = new Blob(this.videoChunks, { type: mimeType });
        console.log('Video recording stopped, blob size:', videoBlob.size, 'type:', mimeType);
        
        // Only cleanup AFTER we have the blob
        this.cleanup();
        
        resolve(videoBlob);
      };

      this.mediaRecorder.stop();
    });
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  cancelRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.cleanup();
  }

  private cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.videoChunks = [];
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }
}

export function getVideoExtension(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4';
  return 'webm';
}
